import React from 'react';

const DISCLAIMER = (
  <>
    <strong>Informational purposes only.</strong> Bidsig provides tools and content for general information only. Nothing on the Service constitutes investment, legal, or tax advice. Seek independent professional advice before making any investment or legal decision.
  </>
);

const Privacy = () => (
  <div className="page-content legal-page">
    <h2>Privacy Policy</h2>
    <p className="legal-updated">Last updated: {new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

    <section>
      <h3>1. Introduction</h3>
      <p>Bidsig (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your privacy. This Privacy Policy describes how we collect, use, disclose, and protect information when you use our Service. It is intended for users in the <strong>United States and Canada</strong>. By using the Service, you consent to the practices described here.</p>
    </section>

    <section>
      <h3>2. Information We Collect</h3>
      <p>We may collect:</p>
      <ul>
        <li><strong>Account information:</strong> name, email address, and other details you provide when registering or updating your profile.</li>
        <li><strong>Content you create:</strong> watchlists, portfolio holdings, custom rules, price alerts, message keywords, and preferences you save in the Service.</li>
        <li><strong>Communications:</strong> support inquiries you send us and, if you subscribe, your newsletter email address and subscription status.</li>
        <li><strong>Usage data:</strong> how you use the Service (e.g., pages visited, features used, and interactions).</li>
        <li><strong>Device and technical data:</strong> IP address, browser type, device type, and similar technical information.</li>
      </ul>
    </section>

    <section>
      <h3>3. How We Use Your Information</h3>
      <p>We use collected information to:</p>
      <ul>
        <li>Provide, maintain, and improve the Service</li>
        <li>Authenticate you and manage your account</li>
        <li>Customize your experience by remembering your watchlists, saved symbols, alerts, and display settings</li>
        <li>Deliver alerts, notifications, and (with your consent) the newsletter</li>
        <li>Respond to your support requests and communicate about the Service, updates, or security</li>
        <li>Analyze usage and improve security and performance</li>
        <li>Comply with applicable law and protect our rights</li>
      </ul>
      <p>AI overviews, news, sentiment, and signals are generated from general models and rules applied uniformly to all users. They are not personalized investment advice and are not based on an assessment of your individual financial circumstances.</p>
    </section>

    <section>
      <h3>4. Cookies and Similar Technologies</h3>
      <p>We use cookies and similar technologies to keep you signed in, remember preferences, and understand how the Service is used. You can manage cookie settings in your browser; some features may not work correctly if you disable cookies.</p>
    </section>

    <section>
      <h3>5. Sharing and Disclosure</h3>
      <p>We do not sell your personal information. We may share information with:</p>
      <ul>
        <li><strong>Service providers</strong> that help us Modify the Service (e.g., cloud hosting, authentication, email delivery, and analytics), under contracts that limit their use to providing services to us</li>
        <li><strong>Legal and regulatory authorities</strong> when required by law or to protect our rights and safety</li>
        <li><strong>Other parties</strong> with your consent or at your direction, or in connection with a business transfer such as a merger or acquisition</li>
      </ul>
    </section>

    <section>
      <h3>6. Data Retention</h3>
      <p>We retain your information for as long as your account is active or as needed to provide the Service, and for a reasonable period thereafter for legal, security, and business purposes. You may request deletion of your account and associated data subject to applicable law.</p>
    </section>

    <section>
      <h3>7. Your Privacy Rights (United States and Canada)</h3>
      <ul>
        <li><strong>United States:</strong> Depending on your state, you may have rights to access, correct, delete, or port your personal information, or to opt out of certain sales or targeted advertising. We do not sell personal information or share it for cross-context behavioral advertising as defined under the California Consumer Privacy Act (CCPA/CPRA) and similar state laws.</li>
        <li><strong>Canada:</strong> You have rights under applicable federal and provincial privacy laws, including the Personal Information Protection and Electronic Documents Act (PIPEDA) and provincial laws such as Quebec&rsquo;s Law 25. These include the right to access and correct your personal information and to withdraw consent, subject to legal and contractual limits.</li>
      </ul>
      <p>To exercise your rights, contact us using the details in the Contact section. We will respond in accordance with applicable law.</p>
    </section>

    <section>
      <h3>8. Marketing Communications</h3>
      <p>We send optional marketing communications, such as the newsletter and configurable alerts, only in accordance with applicable law, including Canada&rsquo;s Anti-Spam Legislation (CASL) and the U.S. CAN-SPAM Act. Newsletter sign-ups use a double opt-in confirmation. You can unsubscribe at any time using the link in any marketing email or through your account settings. We may still send non-marketing account, security, and transactional messages while your account is active.</p>
    </section>

    <section>
      <h3>9. Security</h3>
      <p>We use reasonable technical and organizational measures to protect your information against unauthorized access, loss, or alteration. No method of transmission or storage is completely secure; we cannot guarantee absolute security.</p>
    </section>

    <section>
      <h3>10. Data Processing and Location</h3>
      <p>We are available in the United States and Canada, and your information may be processed and stored on servers located in either country by us or our service providers. Where personal information is transferred across borders (for example, between Canada and the United States), we take steps to ensure appropriate safeguards are in place as required by applicable law.</p>
    </section>

    <section>
      <h3>11. Children</h3>
      <p>The Service is not intended for users under the age of majority in their jurisdiction. We do not knowingly collect personal information from children. If you believe we have collected such information, please contact us so we can delete it.</p>
    </section>

    <section>
      <h3>12. Disclaimer Regarding Service Content</h3>
      <p className="legal-disclaimer">{DISCLAIMER}</p>
    </section>

    <section>
      <h3>13. Changes to This Policy</h3>
      <p>We may update this Privacy Policy from time to time. We will post the revised policy on the Service and update the &ldquo;Last updated&rdquo; date. Continued use after changes constitutes acceptance. For material changes, we may provide additional notice as required by law.</p>
    </section>

    <section>
      <h3>14. Contact</h3>
      <p>For privacy-related questions, to exercise your rights, or to contact our data protection contact, use the contact details provided in the Support section of the Service.</p>
    </section>
  </div>
);

export default Privacy;
